let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.quantityButtons = this.querySelectorAll('.pdp-quantity__button');

        this.quantityButtons.forEach((button) => {
          button.addEventListener('click', this.onQuantityButtonClick.bind(this));
        });

        this.quantityInput?.addEventListener('change', () => {
          this.quantityInput.value = this.normalizedQuantity;
          this.updateQuantityButtons();
        });

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.updateQuantityButtons();

        this.deliveryDatePicker = this.querySelector('delivery-date-picker');

        this.cardOptions = this.querySelectorAll('[data-card-option]');
        this.cardMessageField = this.querySelector('[data-card-message-field]');
        this.cardMessageInput = this.querySelector('[data-card-message]');
        this.cardMessageCount = this.querySelector('[data-card-message-count]');
        this.cardMessageError = this.querySelector('[data-card-message-error]');
        this.cardOptions.forEach((option) => option.addEventListener('change', this.onCardOptionChange.bind(this)));
        this.cardMessageInput?.addEventListener('input', this.onCardMessageInput.bind(this));
        // Sync the message field to the preselected card (and to any choice the
        // browser restored on back/forward navigation).
        if (this.cardOptions.length) this.onCardOptionChange();

        // GoCart handles the add-to-cart click, so validation and the card line item hook in here.
        this.form.addEventListener('cart:before-add', this.onBeforeCartAdd.bind(this));
      }

      get selectedCardOption() {
        return [...this.cardOptions].find((option) => option.checked && option.value) || null;
      }

      onCardOptionChange() {
        const option = this.selectedCardOption;
        this.cardMessageField.toggleAttribute('hidden', !option);
        if (option) this.cardMessageInput.maxLength = Number(option.dataset.cardMaxLength);
        this.showCardMessageError(false);
        this.updateCardMessageCount();
      }

      onCardMessageInput() {
        this.showCardMessageError(false);
        this.updateCardMessageCount();
      }

      updateCardMessageCount() {
        const option = this.selectedCardOption;
        if (!option || !this.cardMessageCount) return;

        const maxLength = Number(option.dataset.cardMaxLength);
        const length = this.cardMessageInput.value.length;
        let text = `${length} / ${maxLength}`;
        if (option.dataset.cardSmall === 'true' && length >= maxLength) {
          text += ` · ${window.greetingCardStrings?.upgradeHint || ''}`;
        }
        this.cardMessageCount.textContent = text;
      }

      validateCardMessage() {
        const option = this.selectedCardOption;
        if (!option) return true;

        const message = this.cardMessageInput.value.trim();
        let error = false;
        if (!message) {
          error = window.greetingCardStrings?.requiredError;
        } else if (message.length > Number(option.dataset.cardMaxLength)) {
          error = window.greetingCardStrings?.tooLongError;
        }

        this.showCardMessageError(error);
        return !error;
      }

      showCardMessageError(message) {
        if (!this.cardMessageError) return;
        this.cardMessageError.toggleAttribute('hidden', !message);
        this.cardMessageError.textContent = message || '';
        this.cardMessageInput.setAttribute('aria-invalid', message ? 'true' : 'false');
      }

      onBeforeCartAdd(event) {
        const deliveryDateValid = !this.deliveryDatePicker || this.deliveryDatePicker.validate();
        const cardMessageValid = this.validateCardMessage();

        if (!deliveryDateValid || !cardMessageValid) {
          event.preventDefault();
          (deliveryDateValid ? this.cardMessageInput : this.deliveryDatePicker).focus();
          return;
        }

        const cardOption = this.selectedCardOption;
        const addonOptions = [...this.querySelectorAll('[data-addon-option]:checked')];
        if (!cardOption && !addonOptions.length) return;

        const { formData } = event.detail;
        const productItem = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity')) || 1,
          properties: {},
        };
        formData.forEach((value, key) => {
          const property = key.match(/^properties\[(.+)\]$/);
          if (property) productItem.properties[property[1]] = value;
        });
        if (formData.get('selling_plan')) productItem.selling_plan = Number(formData.get('selling_plan'));

        const items = [productItem];
        if (cardOption) {
          items.push({
            id: Number(cardOption.value),
            quantity: 1,
            properties: {
              Poruka: this.cardMessageInput.value.trim(),
              Buket: this.dataset.productTitle,
            },
          });
        }
        // Add-ons are only sold with a bouquet; the "Buket" property lists them under it in the cart.
        addonOptions.forEach((option) => {
          items.push({ id: Number(option.value), quantity: 1, properties: { Buket: this.dataset.productTitle } });
        });

        event.detail.body = JSON.stringify({
          items,
          sections: formData.get('sections'),
          sections_url: formData.get('sections_url'),
        });
      }

      onQuantityButtonClick(event) {
        event.preventDefault();
        if (!this.quantityInput) return;

        const currentQuantity = this.normalizedQuantity;
        const increment = Number.parseInt(this.quantityInput.step, 10) || 1;
        const nextQuantity = event.currentTarget.name === 'plus'
          ? currentQuantity + increment
          : currentQuantity - increment;
        this.quantityInput.value = nextQuantity;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateQuantityRules() {
        if (!this.quantityInput) return;
        this.quantityInput.value = this.normalizedQuantity;
        this.updateQuantityButtons();
      }

      updateQuantityButtons() {
        if (!this.quantityInput) return;

        const quantity = this.normalizedQuantity;
        const minimum = Number.parseInt(this.quantityInput.min, 10) || 1;
        const maximum = this.quantityInput.max
          ? Number.parseInt(this.quantityInput.max, 10)
          : Number.POSITIVE_INFINITY;

        this.quantityButtons.forEach((button) => {
          const atMinimum = button.name === 'minus' && quantity <= minimum;
          const atMaximum = button.name === 'plus' && quantity >= maximum;
          button.disabled = atMinimum || atMaximum;
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (!this.variantIdInput.value) {
          this.handleErrorMessage(window.variantStrings.unavailable);
          return;
        }

        if (this.deliveryDatePicker && !this.deliveryDatePicker.validate()) {
          this.deliveryDatePicker.focus();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // This theme's cart is the GoCart side cart: open it rather than
              // leaving the page. The cart page stays only as a fallback.
              if (window.goCart) {
                window.goCart.fetchAndOpenCart();
              } else {
                window.location = window.routes.cart_url;
              }
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.submitButton?.setAttribute('aria-invalid', errorMessage ? 'true' : 'false');

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get normalizedQuantity() {
        const minimum = Number.parseInt(this.quantityInput?.min, 10) || 1;
        const maximum = this.quantityInput?.max
          ? Number.parseInt(this.quantityInput.max, 10)
          : Number.POSITIVE_INFINITY;
        const increment = Number.parseInt(this.quantityInput?.step, 10) || 1;
        const requestedQuantity = Number.parseInt(this.quantityInput?.value, 10) || minimum;
        const normalizedQuantity = minimum + Math.round((requestedQuantity - minimum) / increment) * increment;

        return Math.min(maximum, Math.max(minimum, normalizedQuantity));
      }
    }
  );
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define(
    'selling-plan-picker',
    class SellingPlanPicker extends HTMLElement {
      connectedCallback() {
        this.hiddenInput = this.querySelector('input[name="selling_plan"]');
        this.addEventListener('change', this.handleChange.bind(this));
        this.syncSelectedPlan();
      }

      handleChange(event) {
        if (!event.target.matches('[data-selling-plan-id]')) return;
        this.syncSelectedPlan();
      }

      syncSelectedPlan() {
        if (!this.hiddenInput) return;

        const selectedOption = this.querySelector('[data-selling-plan-id]:checked');
        const sellingPlanId = selectedOption?.value || '';
        this.hiddenInput.value = sellingPlanId;
        this.hiddenInput.disabled = sellingPlanId === '';

        const price = Number(selectedOption?.dataset.price);
        const productInfo = this.closest('product-info');
        if (Number.isFinite(price)) {
          const formattedPrice = window.formatMoney(price);
          const priceElement = productInfo?.querySelector('[data-testid="pdp-actual-price"]');
          const stickyPriceElement = productInfo?.querySelector('[data-sticky-atc-price]');
          if (priceElement) priceElement.textContent = formattedPrice;
          if (stickyPriceElement) stickyPriceElement.textContent = formattedPrice;
        }
      }
    }
  );
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.isSyncing = false; // Flag to prevent recursive sync
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (!this.isSyncing) {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);

        // Publish the change to propagate the selection to other `variant-selects`
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });

        // Synchronize other variant-selects elements
        this.syncOtherVariantSelects();
      }
    });
  }

  syncOtherVariantSelects() {
    const otherVariantSelects = document.querySelectorAll('variant-selects');

    otherVariantSelects.forEach((otherSelect) => {
      if (otherSelect !== this) {
        otherSelect.isSyncing = true; // Prevent recursive sync

        this.selectedOptionValues.forEach((value, index) => {
          const selectElement = otherSelect.querySelectorAll('select')[index];
          if (selectElement) {
            Array.from(selectElement.options).forEach((option) => {
              option.selected = option.dataset.optionValueId === value;
            });
          }
        });

        // Trigger change event for the other select to ensure all associated logic executes
        otherSelect.dispatchEvent(new Event('change'));

        otherSelect.isSyncing = false; // Reset flag after sync
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);
